import { useCallback, useState } from 'react';
import {
  Link,
  Toast,
  ToastBody,
  ToastFooter,
  ToastTitle,
  useToastController,
} from '@fluentui/react-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { logout, switchTenant, type LoginSuccess, type OtpPrompt } from '../api/auth.js';
import { rutaDeVerificacion } from './verificarAcceso.js';
import { ApiClientError } from '../api/client.js';
import { useAuthStore } from '../stores/authStore.js';
import { GLOBAL_TOASTER_ID } from '../components/ThemedProvider.js';
import { RUTA_TRAS_CAMBIO, debeSalirDeLaRuta, describeSwitchTenantError } from './tenantSwitch.js';

export interface DestinoCambio {
  slug: string;
  name: string;
  kind?: 'team' | 'company';
}

/**
 * Cambio de contexto de empresa sin volver a iniciar sesión.
 *
 * Lo comparten la barra superior y el panel del Dashboard para que los dos
 * hagan EXACTAMENTE lo mismo:
 *   1. `switch-tenant` (el servidor revalida el derecho contra la empresa destino);
 *   2. `setSession`, que es lo que dispara la limpieza de trabajos de cotización,
 *      precios y consumo del contexto anterior (ver `AuthBootstrap`);
 *   3. salir de la ruta del contexto anterior si era de un proyecto/catálogo
 *      concreto, para no quedarse mirando algo que ya no se puede ver.
 *
 * Los errores se muestran con un aviso distinto por motivo, y el único que tiene
 * salida (licencia sin comprobar) trae el botón que la ejecuta: cerrar sesión y
 * volver a entrar eligiendo esa empresa.
 */
export function useTenantSwitch() {
  const setSession = useAuthStore((s) => s.setSession);
  const clearSession = useAuthStore((s) => s.clearSession);
  const navigate = useNavigate();
  const location = useLocation();
  const { dispatchToast } = useToastController(GLOBAL_TOASTER_ID);
  const [switchingSlug, setSwitchingSlug] = useState<string | null>(null);

  const switchTo = useCallback(
    async (destino: DestinoCambio): Promise<boolean> => {
      setSwitchingSlug(destino.slug);
      try {
        const data = await switchTenant(destino.slug);
        if ((data as OtpPrompt).code === 'OTP_REQUIRED') {
          // Entrar a una empresa de staff pide el código de verificación.
          const otp = data as OtpPrompt;
          window.location.assign(rutaDeVerificacion({ desafio: otp.desafio, destino: otp.destino, volver: RUTA_TRAS_CAMBIO }));
          return true;
        }
        setSession(data as LoginSuccess);
        if (debeSalirDeLaRuta(location.pathname)) {
          navigate(RUTA_TRAS_CAMBIO, { replace: true });
        }
        return true;
      } catch (err) {
        const info = describeSwitchTenantError(
          err instanceof ApiClientError ? { code: err.code, message: err.message } : null,
          destino,
        );
        dispatchToast(
          <Toast>
            <ToastTitle>{info.title}</ToastTitle>
            <ToastBody>{info.message}</ToastBody>
            {info.reloginPath && (
              <ToastFooter>
                <Link
                  onClick={() => {
                    void (async () => {
                      await logout().catch(() => undefined);
                      clearSession();
                      navigate(info.reloginPath!, { replace: true });
                    })();
                  }}
                >
                  Cerrar sesión y entrar en esa empresa
                </Link>
              </ToastFooter>
            )}
          </Toast>,
          { intent: 'warning', timeout: 12_000 },
        );
        return false;
      } finally {
        setSwitchingSlug(null);
      }
    },
    [clearSession, dispatchToast, location.pathname, navigate, setSession],
  );

  return { switchingSlug, switchTo };
}
